import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { getDataClient } from "@/lib/dataClient";
import { resolvePanelUser } from "@/lib/profile";

type MatchState =
  | { type: "success"; studentName: string }
  | { type: "error"; message: string };

interface ChildProfile {
  id: string;
  full_name: string;
  grade: string;
  city: string;
}

export default function Profil() {
  const location = useLocation();
  const { user } = useAuth();
  const identity = resolvePanelUser(user);
  const [kod, setKod] = useState("");
  const [durum, setDurum] = useState<MatchState | null>(() => typeof location.state?.matchError === "string" ? { type: "error", message: location.state.matchError } : null);
  const [loading, setLoading] = useState(false);
  const [children, setChildren] = useState<ChildProfile[]>([]);
  const [childrenLoading, setChildrenLoading] = useState(true);

  const loadChildren = useCallback(async () => {
    if (!user) {
      setChildren([]);
      setChildrenLoading(false);
      return;
    }
    setChildrenLoading(true);
    try {
      const dataClient = getDataClient();
      const { data, error } = await dataClient
        .from("profiles")
        .select("id, full_name, grade, city")
        .eq("parent_id", user.id)
        .order("created_at", { ascending: true });

      if (error) {
        setChildren([]);
      } else {
        setChildren((data as ChildProfile[]) ?? []);
      }
    } catch {
      setChildren([]);
    } finally {
      setChildrenLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDurum(null);

    const code = kod.trim();
    if (!code) {
      setDurum({ type: "error", message: "Lütfen eşleştirme kodunu girin." });
      return;
    }

    setLoading(true);
    try {
      const dataClient = getDataClient();
      const { data, error } = await dataClient.rpc("match_parent_to_student", {
        p_code: code,
      });

      if (error) {
        setDurum({
          type: "error",
          message: error.message || "Eşleştirme tamamlanamadı. Lütfen tekrar deneyin.",
        });
        return;
      }

      const result = (data ?? {}) as { ok?: boolean; student_name?: string; message?: string };
      if (result.ok) {
        setDurum({
          type: "success",
          studentName: result.student_name || "Öğrenci",
        });
        setKod("");
        await loadChildren();
      } else {
        setDurum({
          type: "error",
          message: result.message || "Eşleştirme tamamlanamadı.",
        });
      }
    } catch {
      setDurum({
        type: "error",
        message: "Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "mt-1.5 w-full h-11 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors uppercase tracking-widest";

  return (
    <div className="space-y-6 max-w-2xl">
      <SectionHeader
        title="Profil"
        description="Hesap ve iletişim bilgilerinizi yönetin."
      />

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-center gap-4">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-950 text-background-50 font-heading font-bold text-xl">
            {identity.initials || "V"}
          </span>
          <div>
            <p className="font-heading font-semibold text-base text-foreground-950">
              {identity.name || "Veli"}
            </p>
            <p className="text-sm text-foreground-500">{identity.email}</p>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-background-100 px-4 py-3">
          <p className="text-sm text-foreground-600">
            İletişim ve bildirim tercihleriniz burada görünür.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-team-line text-primary-600" aria-hidden="true"></i>
          Eşleşen Çocuklar
        </p>
        <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
          Hesabınla eşleştirilen öğrencilerin listesi.
        </p>

        {childrenLoading ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
            Yükleniyor...
          </p>
        ) : children.length === 0 ? (
          <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-sm text-foreground-500">
            Henüz eşleştirilmiş bir çocuk yok. Aşağıdan eşleştirme koduyla bağlayabilirsin.
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {children.map((child) => (
              <li
                key={child.id}
                className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 px-4 py-3"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-semibold text-sm">
                  {initialsOf(child.full_name)}
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-foreground-950">{child.full_name}</p>
                  <p className="text-xs text-foreground-500">
                    {child.grade ? `${child.grade} · ` : ""}
                    {child.city || "Öğrenci"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-link text-primary-600" aria-hidden="true"></i>
          Çocuğunla Eşleştir
        </p>
        <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
          Çocuğunun hesabındaki eşleştirme kodunu yaz. Kod, veli hesabını çocuğunla
          eşleştirir ve gelişimini yakından takip etmeni sağlar.
        </p>

        <form onSubmit={handleSubmit} className="mt-4">
          <label htmlFor="veli-eslestirme-kod" className="block text-sm font-medium text-foreground-800">
            Eşleştirme Kodu
          </label>
          <input
            id="veli-eslestirme-kod"
            name="eslestirme_kodu"
            type="text"
            required
            value={kod}
            onChange={(e) => setKod(e.target.value)}
            placeholder="DD-XXXXXX"
            className={inputClass}
          />

          <button
            type="submit"
            disabled={loading}
            className="mt-4 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <i className={loading ? "ri-loader-4-line animate-spin" : "ri-link"} aria-hidden="true"></i>
            {loading ? "Eşleştiriliyor..." : "Eşleştir"}
          </button>
        </form>

        {durum?.type === "success" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-foreground-700 bg-accent-100 rounded-md p-3 leading-relaxed">
            <i className="ri-checkbox-circle-line text-accent-700 mt-0.5" aria-hidden="true"></i>
            <span>
              <strong className="font-semibold">{durum.studentName}</strong> hesabınla başarıyla
              eşleştirildi. Artık gelişimini veli panelinden takip edebilirsin.
            </span>
          </p>
        )}

        {durum?.type === "error" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-foreground-700 bg-background-100 rounded-md p-3 leading-relaxed">
            <i className="ri-error-warning-line text-accent-700 mt-0.5" aria-hidden="true"></i>
            <span>{durum.message}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 1).toLocaleUpperCase("tr-TR");
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase("tr-TR");
}
